/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { getAnomalies, getIncidents, getOverview } from "@/api/overview";
import { notificationsApi } from "@/api/notifications";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { IncidentsView } from "@/components/homepage/IncidentsModal";
import { CenterTab, useNotificationCenter } from "@/context/notificationCenter";
import { ProblemsPanel } from "./ProblemsPanel";
import { SentPanel } from "./SentPanel";
import { useAuth } from "@/context/auth";
import { cn } from "@/lib/utils";

const DAY = 86_400_000;

/**
 * The bell turns into the panel: the panel's own box (position, size, radius, fill) grows from the bell's box
 * (CSS keyframes in styles/notifications.css read these custom properties), and a copy of the bell's face is
 * drawn above it, outside the panel so nothing clips it, and fades as the frame grows (it fades back in when
 * the panel folds into the bell again).
 */
const MorphFromBell: React.FC = () => {
  const { open } = useNotificationCenter();
  const ref = useRef<HTMLSpanElement>(null);
  const [ghost, setGhost] = useState<{ x: number; y: number; w: number; h: number; html: string } | null>(null);

  useLayoutEffect(() => {
    const panel = ref.current?.closest<HTMLElement>('[data-slot="sheet-content"]');
    const bell = [...document.querySelectorAll<HTMLElement>(".nc-bell")].find((b) => b.getBoundingClientRect().width > 0);
    if (!panel || !bell) return;
    const b = bell.getBoundingClientRect();
    const set = (k: string, v: string) => panel.style.setProperty(k, v);
    set("--nc-t", `${Math.round(b.top)}px`);
    set("--nc-r", `${Math.round(window.innerWidth - b.right)}px`);
    set("--nc-b", `${Math.round(window.innerHeight - b.bottom)}px`);
    set("--nc-bh", `${Math.round(b.height)}px`);
    set("--nc-bw", `${Math.round(b.width)}px`);
    // the final width, measured with the animation off (forcing a style pass would otherwise read the bell's width)
    panel.style.animation = "none";
    set("--nc-pw", `${panel.clientWidth}px`);
    // the panel's final box in numbers: "auto" cannot be animated to, so the keyframes end on these
    const f = panel.getBoundingClientRect();
    const fs = getComputedStyle(panel);
    set("--nc-ft", `${f.top}px`);
    set("--nc-fr", `${window.innerWidth - f.right}px`);
    set("--nc-fb", `${window.innerHeight - f.bottom}px`);
    set("--nc-fw", `${f.width}px`);
    set("--nc-fh", `${f.height}px`);
    set("--nc-rf", `${fs.borderTopLeftRadius} ${fs.borderTopRightRadius} ${fs.borderBottomRightRadius} ${fs.borderBottomLeftRadius}`);
    panel.style.animation = "";
    set("--nc-rb", getComputedStyle(bell).borderTopLeftRadius || "14px");
    set("--nc-bg", getComputedStyle(bell).backgroundColor);
    setGhost({ x: b.left, y: b.top, w: b.width, h: b.height, html: bell.innerHTML });

    // the bell stays on screen until the panel, which starts exactly on its box, has been painted over it;
    // it is back when the panel is gone
    const hide = () => bell.setAttribute("data-morph", "");
    panel.addEventListener("animationstart", hide, { once: true });
    // once it has opened the contents follow the panel's width again (the window can be resized)
    const settle = (e: AnimationEvent) => {
      if (e.target === panel && e.animationName === "nc-morph-in") panel.setAttribute("data-settled", "");
    };
    panel.addEventListener("animationend", settle);
    return () => {
      panel.removeEventListener("animationstart", hide);
      panel.removeEventListener("animationend", settle);
      bell.removeAttribute("data-morph");
    };
  }, []);

  return (
    <span ref={ref} aria-hidden>
      {ghost &&
        createPortal(
          <span
            className={cn("nc-ghost", !open && "nc-ghost--back")}
            style={{ left: ghost.x, top: ghost.y, width: ghost.w, height: ghost.h }}
            dangerouslySetInnerHTML={{ __html: ghost.html }}
          />,
          document.body,
        )}
    </span>
  );
};

/** The notification centre: open problems, incident history and what was sent, in one side panel. */
export const NotificationCenter: React.FC = () => {
  const { open: wantOpen, tab, focus, closeCenter, setTab } = useNotificationCenter();
  // a link can arrive before the sign-in: the panel waits for it
  const { isAuthenticated } = useAuth();
  const open = wantOpen && isAuthenticated;

  // only while it is open: the login page must not fetch anything
  const { data: overview } = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: 30_000, enabled: open });
  const { data: anomalies = [] } = useQuery({ queryKey: ["anomalies"], queryFn: getAnomalies, refetchInterval: 30_000, enabled: open });
  const { data: incidents = [] } = useQuery({ queryKey: ["incidents"], queryFn: getIncidents, refetchInterval: 30_000, enabled: open });
  const { data: sent, dataUpdatedAt: sentAt } = useQuery({
    queryKey: ["notification-history"],
    queryFn: () => notificationsApi.getHistory(200),
    refetchInterval: 30_000,
    enabled: open,
  });

  const problemCount = (overview?.problems.length ?? 0) + anomalies.length;
  const hasCrit = overview?.problems.some((p) => p.severity === "crit") ?? false;
  const failed = (sent ?? []).filter((r) => !r.success && sentAt - new Date(r.created_at).getTime() < DAY).length;

  const tabs: { id: CenterTab; label: string; count?: number; tone?: "crit" | "warn" }[] = [
    { id: "problems", label: "Problems", count: problemCount, tone: hasCrit ? "crit" : "warn" },
    { id: "history", label: "History" },
    { id: "sent", label: "Sent", count: failed, tone: "crit" },
  ];

  return (
    <Sheet open={open} onOpenChange={(o) => !o && closeCenter()}>
      <SheetContent
        side="right"
        className="nc-sheet gap-0 p-0 sm:max-w-[560px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <MorphFromBell />
        <SheetHeader className="nc-head">
          <SheetTitle>Notifications</SheetTitle>
          <SheetDescription className="sr-only">Open problems, incident history and what was sent</SheetDescription>
        </SheetHeader>
        <div className="nc-tabs" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={cn("gh-btn", tab === t.id && "gh-btn--on")}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.count ? <span className={cn("nc-count", t.tone && `nc-count--${t.tone}`)}>{t.count}</span> : null}
            </button>
          ))}
        </div>
        <div className="nc-body">
          {tab === "problems" && <ProblemsPanel focus={focus} />}
          {tab === "history" && <IncidentsView incidents={incidents} />}
          {tab === "sent" && <SentPanel />}
        </div>
      </SheetContent>
    </Sheet>
  );
};
